import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import TablePeo from './TablePeo';
import { exportToExcel } from '../utils/exportUtils';
import {
  AudienceCriteria,
  previewEmailCampaignAudience,
  RecipientPreview,
} from '../endpoints/emailCampaigns';
import type { SortOrder } from '../types/table';

const PREVIEW_HEAD_CELLS = [
  { id: 'last_name', label: 'Last name', firstCell: true, sortable: false },
  { id: 'first_name', label: 'First name', sortable: false },
  { id: 'email', label: 'Email', sortable: false },
];

export interface EmailCampaignPreviewDialogProps {
  open: boolean;
  onClose: () => void;
  audienceCriteria: AudienceCriteria | null;
}

const EmailCampaignPreviewDialog: React.FC<EmailCampaignPreviewDialogProps> = ({
  open,
  onClose,
  audienceCriteria,
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [order, setOrder] = useState<SortOrder>('asc');
  const [orderBy, setOrderBy] = useState('last_name');
  const [items, setItems] = useState<RecipientPreview[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !audienceCriteria) return;
    let cancelled = false;
    setLoading(true);
    previewEmailCampaignAudience(audienceCriteria, page + 1, rowsPerPage)
      .then((res) => {
        if (cancelled) return;
        setItems(res.items);
        setTotalCount(res.total_count);
      })
      .catch(() => {
        if (!cancelled) {
          setItems([]);
          setTotalCount(0);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, audienceCriteria, page, rowsPerPage]);

  useEffect(() => {
    if (open) {
      setPage(0);
    }
  }, [open, audienceCriteria]);

  const tableRows = useMemo(
    () =>
      items.map((r, i) => ({
        id: r.user_id || r.email || String(i),
        last_name: r.last_name || '—',
        first_name: r.first_name || '—',
        email: r.email,
      })),
    [items]
  );

  const handleExport = () => {
    exportToExcel(
      tableRows.map((r) => ({
        last_name: r.last_name,
        first_name: r.first_name,
        email: r.email,
      })),
      [
        { field: 'last_name', headerName: 'Last name' },
        { field: 'first_name', headerName: 'First name' },
        { field: 'email', headerName: 'Email' },
      ],
      { filename: 'email-campaign-preview' }
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Preview recipients</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1 }}>
          <TablePeo
            data={tableRows}
            headCells={PREVIEW_HEAD_CELLS}
            isFetching={loading}
            page={page}
            setPage={setPage}
            rowsPerPage={rowsPerPage}
            setRowsPerPage={setRowsPerPage}
            order={order}
            setOrder={setOrder}
            orderBy={orderBy}
            setOrderBy={setOrderBy}
            pagination
            remoteTotalCount={totalCount}
            emptyMessage="No recipients match this audience."
            size="small"
            toolbar={
              <Button
                size="small"
                variant="outlined"
                onClick={handleExport}
                disabled={!tableRows.length}
              >
                Export Excel
              </Button>
            }
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmailCampaignPreviewDialog;
